import React, { useEffect, useState } from "react";
import {
  Button,
  Tabs,
  message,
  Form,
  Space,
  FormProps,
  Row,
  Col,
  Flex,
} from "antd";
import {
  WizardStep,
  DischargeResponse,
  DischargeField,
  DischargeSection,
} from "../../../interfaces/IDischarge";
import TabPane from "antd/es/tabs/TabPane";
import { renderCommonField } from "../CommonFields";
import {
  CheckOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { IDocumentFields } from "../../../interfaces/ICase";
import dayjs from "dayjs";
import { IDeficiencyCheck } from "../../../interfaces/IPatient";
import { useUserFunctionalities } from "../../../utils/UserFunctionalities";
import type { ValidateErrorEntity } from "rc-field-form/lib/interface"; // Import ValidateError for errorInfo type
import Loader from "../../../utils/Spinners";
import ManualStatus from "../PreAuth/ManualStatusButton";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { getVisitStageStates } from "../../../models/preAuthSlice";
import ClaimIDManually from "../PreAuth/ClaimIDManuallyId";
import ManualPortalStatus from "../PreAuth/ManualPortalStatusButton";
import { errorMessage } from "../../../utils/notificationUtils";

type FieldType = {
  dateOfAdmission: string;
  dateOfDischarge: string;
  remarks: string;
  packages: string;
  roomRentAndNursingCharge: string;
  consultation: string;
  otCharge: string;
  surgeonAndAnesthetist: string;
  investigation: string;
  medicineAndConsumables: string;
  otherHospitalExpenses: string;
  documentUrls: [
    {
      documentType: string;
      documentName: string;
      documentUrl: string;
    }
  ];
  treatingDoctorType: string;
  treatingDoctorName: string;
  treatingDoctorRegistrationNo: string;
  treatingDoctorQualification: string;
  treatingDoctorContactNumber: string;
  dischargeSummaryTreatmentGiven: string;
  dischargeSummaryOperativeFindings: string;
  dischargeSummaryPostOperativeMethod: string;
  dischargeSummaryPostSurgery: string;
  statusAtTimeOfDischarge: string;
  dischargeSummaryReview: string;
  dischargeSummaryAdvice: string;
  surgeryDate: string;
  dischargeFollowUpDate: string;
  dischargeConsultAtBlockName: string;
  dischargeFloor: string;
  dischargeRoomNo: string;
  dischargeStatus: string;
  covidPatientOrNot: boolean;
  lastConsultationReceiptOfReportSameAsIntimation: boolean;
  discharge: boolean;
  death: boolean;
};
interface DischargeProps {
  activeStep: number;
  handleFileChange: any;
  allWizardSteps: WizardStep[];
  onNext: () => void;
  onPrevious: () => void;
  onComplete: (step: number) => void;
  dischargeData: DischargeResponse | null;
  isLoading: boolean;
  error: string | null;
  editMode: boolean;
  onToggleEditMode: () => void;
  onFieldChange: (sectionKey: string, fieldId: string, value: string) => void;
  onSaveChanges: () => void;
  onSaveChangesInEdgePro: () => void;
  editedFields: Record<string, Record<string, string>>;
  caseDischargeId: number | null;
  handleTabsChange?: any;
  uploadedDocumentUrl: Array<IDocumentFields>;
  handleDeleteList: CallableFunction;
  viewFile: CallableFunction;
  handleSetDocumentUrl: CallableFunction;
  handleOpen: any;
  treatingDoctorTypeOptions: Array<{ label: string; value: string }>;
  doctorTypeNameList: any;
  onKeyDown: any;
  handleDownloadDocument: CallableFunction;
  isDownloadingDocument?: boolean;
  downloadingDocumentUrl?: string;
  isSubmitting: boolean;
  isSubmitted: boolean;
  handleDeficiencyModal?: any;
  closeDeficiencyModal?: any;
  deficiencyModal: boolean;
  deficiencyQuestions?: any;
  previewDocumentUrl: string;
  setDocumentName: any;
  handleDeficiencyModalAfterSave: any;
  portalSubmission: string | null;
  starHealthRoomTypeOptions: Array<{ id: number; roomType: string }> | null;
  handleIntimateSubmit: any;
  auth: any;
  nextStage: any;
  setMoveToNext: CallableFunction;
  preAuth: any;
  handleApprove: any;
  isApproved: boolean;
  isApprovedInitiated: boolean;
}

const Discharge: React.FC<DischargeProps> = ({
  activeStep,
  allWizardSteps,
  onNext,
  onPrevious,
  onComplete,
  dischargeData,
  isLoading,
  handleFileChange,
  editMode,
  onToggleEditMode,
  onFieldChange,
  onSaveChanges,
  onSaveChangesInEdgePro,
  editedFields,
  error,
  caseDischargeId,
  handleTabsChange,
  uploadedDocumentUrl,
  handleDeleteList,
  viewFile,
  handleSetDocumentUrl,
  handleOpen,
  treatingDoctorTypeOptions,
  doctorTypeNameList,
  onKeyDown,
  handleDownloadDocument,
  isDownloadingDocument,
  downloadingDocumentUrl,
  isSubmitting,
  isSubmitted,
  handleDeficiencyModal,
  closeDeficiencyModal,
  deficiencyModal,
  deficiencyQuestions,
  previewDocumentUrl,
  setDocumentName,
  handleDeficiencyModalAfterSave,
  portalSubmission,
  starHealthRoomTypeOptions,
  handleIntimateSubmit,
  auth,
  nextStage,
  setMoveToNext,
  preAuth,
  handleApprove,
  isApproved,
  isApprovedInitiated,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const [enabledTabs, setEnabledTabs] = useState<number[]>([]);
  const { hasFunctionality, reloadFunctionalities } = useUserFunctionalities();
  const [isGovt, setIsGovt] = useState<boolean>(false);

  const patientStore = useSelector((state: RootState) => state.patients);
  const { stepperData, currentStep, stageAllState } = useSelector(
    (state: RootState) => state.preAuth
  );

  const currentStageDetails = stepperData?.currentVisitStage;
  const currentActiveStage = stepperData?.visitStages[currentStep];

  const customStatus =
    currentActiveStage?.step && currentStageDetails?.step
      ? currentActiveStage.step === currentStageDetails.step
        ? currentStageDetails.stateId
        : currentActiveStage.step > currentStageDetails.step
        ? 8
        : 4
      : null;

  useEffect(() => {
    if (
      patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
        "CGHS" ||
      patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
        "CAPF" ||
      patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
        "ECHS"
    ) {
      setIsGovt(true);
    }
  }, [patientStore?.fetchBasicDetails]);

  useEffect(() => {
    if (currentActiveStage?.visitStageId) {
      dispatch(
        getVisitStageStates({ visitStageId: currentActiveStage?.visitStageId })
      );
    }
  }, [currentActiveStage?.visitStageId]);

  useEffect(() => {
    // Optionally reload functionalities when component mounts
    reloadFunctionalities();
  }, [reloadFunctionalities]);

  const validateForm = async () => {
    try {
      await form.validateFields();
      setIsFormValid(true);
    } catch (errorInfo) {
      setIsFormValid(false);
    }
  };

  useEffect(() => {
    // Set form values from editedFields
    // if (editedFields?.cghsDischargeResponse) {
    //   form.setFieldsValue(editedFields?.cghsDischargeResponse);
    // } else if (editedFields?.ihxDischargeResponses) {
    //   form.setFieldsValue(editedFields?.ihxDischargeResponses);
    // }

    if (editedFields?.cghsDischargeResponse) {
      const updatedFields = Object.keys(
        editedFields.cghsDischargeResponse
      ).reduce((acc, key) => {
        if (key.toLowerCase().includes("date")) {
          acc[key] = editedFields.cghsDischargeResponse[key]
            ? dayjs(
                editedFields.cghsDischargeResponse[key],
                "DD-MM-YYYY HH:mm:ss"
              )
            : null;
        } else {
          acc[key] = editedFields.cghsDischargeResponse[key]; // Retain the original value for other fields
        }
        return acc;
      }, {} as Record<string, any>);

      form.setFieldsValue(updatedFields);
    } else if (editedFields?.ihxDischargeResponses) {
      const updatedFields = Object.keys(
        editedFields.ihxDischargeResponses
      ).reduce((acc, key) => {
        if (key.toLowerCase().includes("date")) {
          acc[key] = editedFields.ihxDischargeResponses[key]
            ? dayjs(
                editedFields.ihxDischargeResponses[key],
                "DD-MM-YYYY HH:mm:ss"
              )
            : null;
        } else {
          acc[key] = editedFields.ihxDischargeResponses[key]; // Retain the original value for other fields
        }
        return acc;
      }, {} as Record<string, any>);

      form.setFieldsValue(updatedFields);
    } else if (editedFields?.starHealthDischargeResponse) {
      const updatedFields = Object.keys(
        editedFields.starHealthDischargeResponse
      ).reduce((acc, key) => {
        if (key.toLowerCase().includes("date")) {
          acc[key] = editedFields.starHealthDischargeResponse[key]
            ? dayjs(
                editedFields.starHealthDischargeResponse[key],
                "DD-MM-YYYY HH:mm:ss"
              )
            : null;
        } else if (key?.toLowerCase()?.includes("roomtype")) {
          if (
            editedFields?.starHealthDischargeResponse[key] &&
            editedFields?.starHealthDischargeResponse[key]?.includes(":")
          ) {
            const roomType: any =
              editedFields?.starHealthDischargeResponse[key]?.split(":");
            acc[key] = roomType?.[0] + "~" + roomType?.[1];
          }
        } else {
          acc[key] = editedFields.starHealthDischargeResponse[key];
        }
        return acc;
      }, {} as Record<string, any>);

      form.setFieldsValue(updatedFields);
    } else if (editedFields?.echsDischargeResponse) {
      const updatedFields = Object.keys(
        editedFields.echsDischargeResponse
      ).reduce((acc, key) => {
        if (key.toLowerCase().includes("date")) {
          acc[key] = editedFields.echsDischargeResponse[key]
            ? dayjs(
                editedFields.echsDischargeResponse[key],
                "DD-MM-YYYY HH:mm:ss"
              )
            : null;
        } else {
          acc[key] = editedFields.echsDischargeResponse[key];
        }
        return acc;
      }, {} as Record<string, any>);

      form.setFieldsValue(updatedFields);
    }
  }, [editedFields, form]);

  useEffect(() => {
    if (!isSubmitted) {
      validateForm();
    }
  }, [form, editedFields]);

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      if (!deficiencyModal) {
        onSaveChanges();
      }
    } catch (errorInfo) {
      const validationError = errorInfo as ValidateErrorEntity<any>; // Correctly type `errorInfo`

      // Check if there are "required" errors
      const requiredErrors = validationError.errorFields.some(
        (error: { errors: string[] }) =>
          error.errors.some((msg: string) => msg.includes("is required"))
      );

      // Display a specific message based on the type of error
      if (requiredErrors) {
        message.error("Please fill in all required fields");
      } else {
        message.error(
          "Please correct the highlighted errors before submitting"
        );
      }
    }
  };

  const handleSubmitForSavetoEdge = async () => {
    try {
      await form.validateFields();
      if (!deficiencyModal) {
        onSaveChangesInEdgePro();
      }
    } catch (errorInfo) {
      const validationError = errorInfo as ValidateErrorEntity<any>; // Correctly type `errorInfo`

      // Check if there are "required" errors
      const requiredErrors = validationError.errorFields.some(
        (error: { errors: string[] }) =>
          error.errors.some((msg: string) => msg.includes("is required"))
      );

      // Display a specific message based on the type of error
      if (requiredErrors) {
        message.error("Please fill in all required fields");
      } else {
        message.error(
          "Please correct the highlighted errors before submitting"
        );
      }
    }
  };

  // useEffect(() => {

  //   if(activeStep) {
  //     validateForm();
  //   }
  // }, [activeStep,form, editedFields]);

  const areAllMandatoryFieldsFilled = () => {
    if (!dischargeData) return false;

    const currentTabTitle = allWizardSteps[activeStep]?.title;
    let currentTabFields: DischargeField[] = [];

    Object.values(dischargeData)?.forEach((sections: DischargeSection[]) => {
      sections?.forEach((section) => {
        if (section[currentTabTitle]) {
          currentTabFields = section[currentTabTitle];
        }
      });
    });

    return currentTabFields?.every((field) => {
      if (field?.required) {
        // Access the value of typeOfDischarge from editedFields (check if it's an array)
        const typeOfDischargeValue =
          editedFields?.cghsDischargeResponse?.typeOfDischarge ||
          editedFields?.echsDischargeResponse?.typeOfDischarge;
        const routineDischargeByValue =
          editedFields?.cghsDischargeResponse?.routineDischarge ||
          editedFields?.echsDischargeResponse?.routineDischarge;
        const assuredByValue =
          editedFields?.cghsDischargeResponse?.assuredBy ||
          editedFields?.echsDischargeResponse?.assuredBy;

        // If typeOfDischarge is an array, use the first element
        const typeOfDischarge = Array.isArray(typeOfDischargeValue)
          ? typeOfDischargeValue[0]
          : typeOfDischargeValue;
        const routineDischargeBy = Array.isArray(routineDischargeByValue)
          ? routineDischargeByValue[0]
          : routineDischargeByValue;
        const assuredBy = Array.isArray(assuredByValue)
          ? assuredByValue[0]
          : assuredByValue;

        // If typeOfDischarge is "Against Medical Advice" or "Death", skip routineDischarge validation

        if (
          (typeOfDischarge === "Against Medical Advice" ||
            typeOfDischarge === "Death") &&
          (field?.id === "routineDischarge" ||
            field?.id === "assuredBy" ||
            field?.id === "nameOfAssurer" ||
            field?.id === "designationOfAssurer" ||
            field?.id == "relationOfPatientToAssurer")
        ) {
          return true; // Skip checking routineDischarge if typeOfDischarge is "Against Medical Advice" or "Death" NameOfAssurer or designationOfAssurer or relationOfPatientToAssurer
        }

        if (
          ((routineDischargeBy?.toLowerCase() ===
            "formality is completed & patient is leaving" ||
            routineDischargeBy?.toLowerCase() ===
              "patient paying caution deposit/bill & leaving") &&
            field?.id === "assuredBy") ||
          field?.id === "nameOfAssurer" ||
          field?.id === "designationOfAssurer" ||
          field?.id == "relationOfPatientToAssurer"
        ) {
          return true; // Skip checking routineDischarge if typeOfDischarge is "Against Medical Advice" or "Death" NameOfAssurer or designationOfAssurer or relationOfPatientToAssurer
        }

        if (
          assuredBy?.toLowerCase() === "other" &&
          (field?.id === "nameOfAssurer" ||
            field?.id === "designationOfAssurer" ||
            field?.id == "relationOfPatientToAssurer")
        ) {
          return true; // Skip checking routineDischarge if typeOfDischarge is "Against Medical Advice" or "Death" NameOfAssurer or designationOfAssurer or relationOfPatientToAssurer
        }

        const value = editedFields[Object.keys(editedFields)[0]]?.[field?.id];
        return value !== undefined && value !== null && value !== "";
      }
      return true;
    });
  };

  const areAllMandatoryTabsFilled = (stepTitle: string, stepIndex: number) => {
    if (!dischargeData) return false;

    let currentTabFields: DischargeField[] = [];
    // const hasMandatoryFields = tabs[stepIndex]?.mandatoryFields?.length > 0;

    Object.values(dischargeData)?.forEach((sections: DischargeSection[]) => {
      sections?.forEach((section) => {
        if (section[stepTitle]) {
          currentTabFields = section[stepTitle];
        }
      });
    });

    return currentTabFields?.every((field) => {
      if (field?.required) {
        const value = editedFields[Object.keys(editedFields)?.[0]]?.[field?.id];
        return value !== undefined && value !== null && value !== "";
      } else if (activeStep < stepIndex || !areAllMandatoryFieldsFilled) {
        return false;
      }
      return true;
    });
  };

  // useEffect(() => {
  //   const newEnabledTabs = allWizardSteps
  //     ?.filter((step, idx) => areAllMandatoryTabsFilled(step?.title, idx))
  //     ?.map((step) => step?.step);
  //   setEnabledTabs(newEnabledTabs);
  // }, [editedFields, allWizardSteps, dischargeData]);

  const areAllMandatoryFieldsTabFilled = (stepTitle: string) => {
    if (!dischargeData) return false;

    let currentTabFields: DischargeField[] = [];

    Object.values(dischargeData)?.forEach((sections: DischargeSection[]) => {
      sections?.forEach((section) => {
        if (section[stepTitle]) {
          currentTabFields = section[stepTitle];
        }
      });
    });

    return currentTabFields?.every((field) => {
      if (field?.required) {
        const value = editedFields[Object.keys(editedFields)[0]]?.[field?.id];
        return value !== undefined && value !== null && value !== "";
      }
      return true;
    });
  };

  useEffect(() => {
    const newEnabledTabs = [0]; // Always enable the first tab
    for (let i = 1; i <= activeStep; i++) {
      if (areAllMandatoryFieldsTabFilled(allWizardSteps[i - 1]?.title)) {
        newEnabledTabs.push(i);
      } else {
        break; // Stop enabling tabs if a step with unfilled mandatory fields is found
      }
    }
    setEnabledTabs(newEnabledTabs);
  }, [editedFields, allWizardSteps, dischargeData, activeStep]);
  const [isTypeOfDischarge, setIsTypeOfDischarge] = useState<boolean>(false);
  const [isRoutineDischarge, setIsRoutineDischarge] = useState<boolean>(false);

  const [isAssuredBy, setIsAssuredBy] = useState<boolean>(false);

  useEffect(() => {
    for (const sectionKey in editedFields) {
      if (
        editedFields[sectionKey]?.typeOfDischarge?.toLowerCase() ===
        "routine discharge"
      ) {
        setIsTypeOfDischarge(true);
      } else {
        setIsTypeOfDischarge(false);
      }
    }
  }, [editedFields]);
  useEffect(() => {
    for (const sectionKey in editedFields) {
      if (
        editedFields[sectionKey]?.routineDischarge?.toLocaleLowerCase() ===
        "patient leaving on assurance"
      ) {
        setIsRoutineDischarge(true);
      } else {
        setIsRoutineDischarge(false);
        setIsAssuredBy(false);
      }
    }
  }, [editedFields]);
  useEffect(() => {
    for (const sectionKey in editedFields) {
      if (
        editedFields[sectionKey]?.assuredBy?.toLowerCase() ===
          "hospital staff" &&
        isRoutineDischarge
      ) {
        setIsAssuredBy(true);
      } else {
        setIsAssuredBy(false);
      }
    }
  }, [editedFields]);

  const renderTabContent = (stepTitle: string) => {
    if (!dischargeData) {
      return <div>No data available</div>;
    }

    const responseKeys = [
      "ihxDischargeResponses",
      "cghsDischargeResponse",
      "echsDischargeResponse",
      "starHealthDischargeResponse",
      "vidalDischargeResponse",
    ];

    for (const key of responseKeys) {
      const data = dischargeData[key as keyof DischargeResponse];
      if (data) {
        const sectionData = (data as DischargeSection[])[0]?.[stepTitle];
        if (sectionData) {
          return (
            <Form
              form={form}
              className="dischargeScheme"
              // onFinish={handleSubmit}
              labelAlign="left"
              labelWrap
              labelCol={{
                xs: { span: 24 },
                sm: { span: 12 },
                md: { span: 6 },
                lg: { span: 6 },
                style: { fontWeight: "600" },
              }}
              wrapperCol={{
                xs: { span: 24 },
                sm: { span: 16 },
                md: { span: 10 },
                lg: { span: 12 },
              }}
              style={{ width: "100%" }}
            >
              {sectionData?.map((field: DischargeField, index: number) => (
                <Row>
                  {renderCommonField({
                    field,
                    index,
                    isReadOnly: !editMode || !field?.updatable,
                    onFieldChange: (_, fieldId, value) =>
                      onFieldChange(key, fieldId, value),
                    handleFileChange,
                    value: editedFields[key]?.[field.id] ?? field.data,
                    isFileUploadDisabled: !editMode, // Check could be wrong for cghs
                    isTypeOfDischarge:
                      field?.id == "typeOfDischarge" && field?.caseDischargeId
                        ? true
                        : isTypeOfDischarge,
                    isRoutineDischarge:
                      field?.id == "routineDischarge" && field?.caseDischargeId
                        ? true
                        : isRoutineDischarge,
                    isAssuredBy:
                      field?.id == "assuredBy" && field?.caseDischargeId
                        ? true
                        : isAssuredBy,
                    key: key,
                    form: form,
                    uploadedDocumentUrl: uploadedDocumentUrl,
                    viewFile: viewFile,
                    handleDeleteList: handleDeleteList,
                    handleSetDocumentUrl: handleSetDocumentUrl,
                    handleOpen: handleOpen,
                    treatingDoctorTypeOptions: treatingDoctorTypeOptions,
                    doctorTypeNameList: doctorTypeNameList,
                    onKeyDown: onKeyDown,
                    handleDownloadDocument: handleDownloadDocument,
                    isDownloadingDocument: isDownloadingDocument,
                    downloadingDocumentUrl: downloadingDocumentUrl,
                    handleDeficiencyModal: handleDeficiencyModal,
                    closeDeficiencyModal: closeDeficiencyModal,
                    deficiencyModal: deficiencyModal,
                    deficiencyQuestions: deficiencyQuestions,
                    previewDocumentUrl: previewDocumentUrl,
                    setDocumentName: setDocumentName,
                    handleDeficiencyModalAfterSave:
                      handleDeficiencyModalAfterSave,
                    starHealthRoomTypeOptions: starHealthRoomTypeOptions,
                  })}
                </Row>
              ))}
            </Form>
          );
        }
      }
    }

    return <div>No data for this section</div>;
  };

  if (isLoading) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div
      style={{
        border: "0.4px solid rgb(87, 87, 87)",
        gap: "15px",
        borderRadius: "16px",
        margin: "0px 0.5rem",
      }}
    >
      <div
        style={{
          minHeight: "64px",
          background: "rgb(243, 243, 243)",
          padding: "12px 35px", // Removed the semicolon and fixed the gap
          gap: "42px", // Added the gap as a separate property
          borderRadius: "16px 16px 0px 0px", // Fixed camelCase
          borderBottom: "1px solid rgb(166, 166, 166)",
          fontWeight: "600",
          fontSize: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: "20px", alignContent: "center" }}>
          DISCHARGE
        </span>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "20px",
            minWidth: "676px",
            maxHeight: "36px",
          }}
        >
          <ClaimIDManually />

          {currentActiveStage?.visitStageId &&
            patientStore?.fetchBasicDetails?.visitInfoResponse?.id && (
              <ManualStatus
                stateData={
                  stageAllState?.[currentActiveStage?.visitStageId] || null
                }
                status={customStatus}
                visitId={patientStore?.fetchBasicDetails?.visitInfoResponse?.id}
                visitStageId={currentActiveStage?.visitStageId}
                // isUpdateable={customStatus == 4 ? false : true}
                isUpdateable={true}
              />
            )}
          {(patientStore?.fetchBasicDetails?.visitInfoResponse?.portalStatus ||
            true) && (
            <ManualPortalStatus
              status={
                patientStore?.fetchBasicDetails?.visitInfoResponse
                  ?.portalStatus || "-"
              }
              visitId={patientStore?.fetchBasicDetails?.visitInfoResponse?.id}
              visitStageId={currentActiveStage?.visitStageId}
              isUpdateable={
                hasFunctionality("portalStatus") && !isGovt ? true : false
              }
              // isUpdateable={true}
            />
          )}
        </div>
      </div>
      <div style={{ padding: "20px 45px" }}>
        <div
          style={{
            position: "sticky",
            top: 0,
            backgroundColor: "white",
            zIndex: 998,
            padding: "10px 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "end",
          }}
        >
          <Space>
            {caseDischargeId &&
            auth?.role?.role != "FO_STAFF" &&
            (portalSubmission === "save as draft" ||
              portalSubmission === null) ? (
              editMode ? (
                <>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={() => {
                      handleSubmitForSavetoEdge();
                    }}

                    // disabled={!isFormValid}
                  >
                    Save
                  </Button>
                </>
              ) : (
                <Button icon={<EditOutlined />} onClick={onToggleEditMode}>
                  Edit
                </Button>
              )
            ) : (
              <></>
            )}
          </Space>
        </div>

        <Tabs
          activeKey={activeStep.toString()}
          type="card"
          size="large"
          style={{ width: "100%" }}
          onChange={handleTabsChange}
          items={allWizardSteps?.map((step) => ({
            label: step?.title,
            key: step?.step?.toString(),
            children: renderTabContent(step?.title),
            disabled: editMode && !enabledTabs.includes(step?.step),
          }))}
        />

        <div style={{ marginTop: 16 }}>
          {activeStep === 1 &&
            (patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() !==
              "ECHS" &&
              patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() !==
                "STAR HEALTH") &&
            !(
              portalSubmission?.toLocaleLowerCase()?.includes("completed") ||
              portalSubmission?.toLocaleLowerCase()?.includes("completed")
            ) &&
            hasFunctionality("dischargeIntimation") &&
            (editMode || auth?.role?.role == "FO_STAFF") &&
            (auth?.role?.role == "FO_STAFF" &&
            ((preAuth?.stepperData?.currentVisitStage?.visitStage ==
              "Discharge" &&
              (preAuth?.stepperData?.currentVisitStage?.state === "Intimated" ||
                preAuth?.stepperData?.currentVisitStage?.state ===
                  "Completed" ||
                preAuth?.stepperData?.currentVisitStage?.state ===
                  "Submitted" ||
                preAuth?.stepperData?.currentVisitStage?.state ===
                  "Initiated")) ||
              preAuth?.stepperData?.currentVisitStage?.visitStageId >
                preAuth?.stepperData?.visitStages[preAuth?.currentStep]
                  ?.visitStageId) ? (
              <></>
            ) : (
              <Button
                type="primary"
                loading={isSubmitting}
                style={{
                  background: "#008B8B",
                  marginRight: "8",
                  float: "right",
                }}
                // disabled={
                //   auth?.role?.role == "FO_STAFF" && caseDischargeId !== null
                // }
                onClick={async () => {
                  try {
                    await form.validateFields();
                    handleIntimateSubmit();
                  } catch (err: any) {
                    if (err?.errorFields) {
                      errorMessage(
                        "Please, Fill All details before submitting"
                      );
                    } else {
                      errorMessage("Please, Try again later");
                    }
                  }
                }}
              >
                Intimate Discharge
              </Button>
            ))}

          {activeStep === 2 &&
            (patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
              "ECHS" ||
              patientStore?.fetchBasicDetails?.casesResponse?.schemeMetaDataName?.toUpperCase() ===
                "STAR HEALTH") &&
            (dischargeData?.echsDischargeResponse ||
              dischargeData?.starHealthDischargeResponse) &&
            !(
              portalSubmission?.toLocaleLowerCase()?.includes("completed") ||
              portalSubmission?.toLocaleLowerCase()?.includes("completed")
            ) &&
            hasFunctionality("dischargeIntimation") &&
            (editMode || auth?.role?.role === "FO_STAFF") &&
            (auth?.role?.role === "FO_STAFF" &&
            ((preAuth?.stepperData?.currentVisitStage?.visitStage ==
              "Discharge" &&
              (preAuth?.stepperData?.currentVisitStage?.state === "Intimated" ||
                preAuth?.stepperData?.currentVisitStage?.state ===
                  "Completed" ||
                preAuth?.stepperData?.currentVisitStage?.state ===
                  "Submitted" ||
                preAuth?.stepperData?.currentVisitStage?.state ===
                  "Initiated")) ||
              preAuth?.stepperData?.currentVisitStage?.visitStageId >
                preAuth?.stepperData?.visitStages[preAuth?.currentStep]
                  ?.visitStageId) ? (
              <></>
            ) : (
              <Button
                type="primary"
                loading={isSubmitting}
                style={{
                  background: "#008B8B",
                  marginRight: "8",
                  float: "right",
                }}
                // disabled={
                //   auth?.role?.role == "FO_STAFF" && caseDischargeId !== null
                // }
                onClick={async () => {
                  try {
                    await form.validateFields();
                    handleIntimateSubmit();
                  } catch (err: any) {
                    if (err?.errorFields) {
                      errorMessage(
                        "Please, Fill All details before submitting"
                      );
                    } else {
                      errorMessage("Please, Try again later");
                    }
                  }
                }}
              >
                Intimate Discharge
              </Button>
            ))}

          <>
            {activeStep === allWizardSteps?.length - 1 && (
              <>
                {/* Render Save Button only if caseDischargeId is not present */}

                {/* Render Submit Button without caseDischargeId check */}

                {hasFunctionality("dischargeSubmission") &&
                  !hasFunctionality("dischargeReview") &&
                  (portalSubmission === "save as draft" ||
                    portalSubmission === null) && (
                    <Button
                      style={{
                        background: "#008B8B",
                        float: "right",
                        marginRight: 8,
                      }}
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={handleSubmit}
                      loading={isSubmitting}
                    >
                      Submit on Portal
                    </Button>
                  )}

                {hasFunctionality("dischargeApprover") &&
                  (portalSubmission === "save as draft" ||
                    portalSubmission === null) && (
                    <Button
                      style={{
                        background: "#008B8B",
                        marginRight: "8px",
                        float: "right",
                      }}
                      disabled={isApproved}
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={handleApprove}
                      loading={isApprovedInitiated}
                    >
                      Approve Discharge
                    </Button>
                  )}

                {!caseDischargeId &&
                  auth?.role?.role != "FO_STAFF" &&
                  !hasFunctionality("dischargeReview") && (
                    <Button
                      style={{
                        background: "#008B8B",
                        marginRight: "8px",
                        float: "right",
                      }}
                      type="primary"
                      onClick={handleSubmitForSavetoEdge}
                      loading={isSubmitting}
                    >
                      Save As Draft
                    </Button>
                  )}
              </>
            )}
          </>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              marginTop: "1rem",
            }}
          >
            {activeStep !== 0 && (
              <Button
                style={{
                  color: "#008B8B",
                  background: "white",
                  border: "1px solid #008B8B",
                  boxShadow: "0px 2px 8px 0px #00000040",
                  borderRadius: "10px",
                  padding: "12px 25px",
                  marginRight: "0.8rem",
                }}
                onClick={onPrevious}
                // style={{ marginRight: 8 }}
              >
                Previous
              </Button>
            )}

            {activeStep !== allWizardSteps?.length - 1 && (
              <Button
                style={{
                  background: "#008B8B",
                  border: "1px solid #008B8B",
                  boxShadow: "0px 2px 8px 0px #00000040",
                  borderRadius: "10px",
                  padding: "12px 25px",
                  textTransform: "uppercase",
                  color: "white",
                  marginRight: "0.8rem",
                }}
                onClick={() => {
                  if (activeStep === allWizardSteps?.length - 1) {
                    onComplete(activeStep);
                  } else {
                    onNext();
                  }
                }}
                disabled={
                  editMode &&
                  (!allWizardSteps[activeStep]?.canNavigate ||
                    !areAllMandatoryFieldsFilled())
                }
              >
                {activeStep === allWizardSteps?.length - 1
                  ? "Submit"
                  : "Save And Continue"}
              </Button>
            )}

            {activeStep == allWizardSteps?.length - 1 &&
              !(
                portalSubmission === "save as draft" ||
                portalSubmission === null
              ) && (
                <Button
                  style={{
                    background: "#008B8B",
                    border: "1px solid #008B8B",
                    boxShadow: "0px 2px 8px 0px #00000040",
                    borderRadius: "10px",
                    padding: "12px 25px",
                    textTransform: "uppercase",
                    color: "white",
                    marginRight: "8px",
                  }}
                  onClick={() => {
                    setMoveToNext(true);
                    // if (activeStep === allWizardSteps?.length - 1) {
                    //   onComplete(activeStep);
                    // } else {
                    //   onNext();
                    // }
                  }}
                  // disabled={
                  //   editMode &&
                  //   (!allWizardSteps[activeStep]?.canNavigate ||
                  //     !areAllMandatoryFieldsFilled())
                  // }
                >
                  NEXT: {nextStage?.visitStage}
                  {/* {activeStep === allWizardSteps?.length - 1 ? "Submit" : "Next"} */}
                </Button>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Discharge;
